import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import './VulpinSuitePage.css';

const PAGE_URL = 'https://e-asy.es/vulpin';

const FAQ_ITEMS = [
  {
    question: '¿Vulpin Suite es un ERP genérico?',
    answer:
      'No. Es un ERP modular pensado sobre todo para organizaciones que manejan datos especialmente sensibles: dependencia, menores, mujer, residencias y pymes de servicios. Business, Social, Care, Corps y Teams se activan según el caso; no es un pack único que “vale para todos”.',
  },
  {
    question: '¿La automatización de procesos y el OCR van aparte?',
    answer:
      'No. La automatización de procesos de gestión (flujos, expedientes, tareas repetidas de administración) y la digitalización / OCR documental son funcionalidades del producto, no servicios sueltos. La automatización con agentes de IA es otra línea (IA en producción / agentes).',
  },
  {
    question: '¿Cuántas organizaciones lo usan?',
    answer:
      'Vulpin Suite está en producción en unas 40 organizaciones, principalmente del sector social y sociosanitario. Referencias públicas: ACUFADE, AFFA (Alzheimer Fuerteventura) y MPDL.',
  },
  {
    question: '¿Y si el dato no puede salir a la nube o hay que cumplir RGPD / AI Act?',
    answer:
      'Ahí entra Ciberseguridad y cumplimiento, y si hace falta un modelo que no salga del perímetro, IA privada. Vulpin es la base de gestión; la seguridad y la IA se apoyan en ese expediente, no al revés.',
  },
  {
    question: '¿Y si lo que buscamos es Dynamics 365 Business Central?',
    answer:
      'Esa es otra ficha: implantación Business Central cuando el ERP tiene que vivir en Microsoft 365. Vulpin no se vende como Dynamics. Si el caso es sociosanitario o de expediente sensible, partimos de Vulpin; si encaja BC, te lo decimos.',
    href: '/servicios/business-central',
    linkLabel: 'Ver ficha Business Central',
  },
];

const VulpinSuitePage = ({ darkMode, toggleDarkMode }) => {
  const modulos = [
    {
      id: 'business',
      titulo: 'Business',
      descripcion:
        'Para pymes: facturación, tesorería y contabilidad en un solo sitio. Menos Excel paralelo y una cifra que cuadra en la reunión.',
      puntos: [
        'Facturación y cobros',
        'Tesorería y seguimiento de caja',
        'Contabilidad operativa',
        'Automatización de procesos de gestión (albaranes, aprobaciones, recordatorios)',
      ],
    },
    {
      id: 'social',
      titulo: 'Social',
      descripcion:
        'Servicios sociales, dependencia, menores y mujer. El expediente, la intervención y la justificación sin duplicar carpetas.',
      puntos: [
        'Gestión de casos e intervenciones',
        'Dependencia, infancia y recursos de mujer',
        'Trazabilidad para memorias y ayudas',
        'Permisos por rol: quién ve qué en un dato de categoría especial',
      ],
    },
    {
      id: 'care',
      titulo: 'Care',
      descripcion:
        'Centros residenciales y geriátricos: plazas, residentes, turnos y seguimiento clínico-social en el mismo entorno.',
      puntos: [
        'Plazas y ocupación',
        'Expediente de la persona residente',
        'Equipos y continuidad entre turnos',
        'Documentación que se puede restaurar y auditar',
      ],
    },
    {
      id: 'corps',
      titulo: 'Corps',
      descripcion:
        'Gestión de voluntariado: personas, disponibilidad, actividad y el rastro de quién ha estado dónde — sin un grupo de WhatsApp como sistema.',
      puntos: [
        'Altas y perfiles de voluntariado',
        'Disponibilidad y asignación',
        'Actividad y evidencias',
        'Coordinación con Social cuando el voluntariado entra en el caso',
      ],
    },
    {
      id: 'teams',
      titulo: 'Teams',
      descripcion:
        'Portal de empleados: fichar, pedir un permiso y consultar la nómina sin un Excel de RRHH ni un grupo de WhatsApp. El equipo ve lo suyo; administración ve el cuadro.',
      puntos: [
        'Control horario y fichaje',
        'Permisos, ausencias y aprobaciones',
        'Nóminas y documentos del trabajador',
        'Portal del empleado, sin montar otro sistema de RRHH',
      ],
    },
  ];

  const incluidoEnProducto = [
    {
      titulo: 'Automatización de procesos de gestión',
      descripcion:
        'Flujos que ya no deberían ser un servicio aparte: expedientes que avanzan, avisos, menos copiar y pegar entre administración y atención. No es un agente de IA suelto; es el ERP haciendo el trabajo repetido.',
    },
    {
      titulo: 'Digitalización y OCR documental',
      descripcion:
        'Papel, PDFs y justificantes que entran al expediente sin reescribirlos a mano. La digitalización documental vive dentro de Vulpin Suite, no como categoría comercial independiente.',
    },
  ];

  const referencias = [
    {
      nombre: 'ACUFADE',
      sector: 'Dependencia y cuidadores familiares',
      modulo: 'Social / Care — casos y seguimiento',
      web: 'https://www.acufade.org/',
    },
    {
      nombre: 'AFFA (Alzheimer Fuerteventura)',
      sector: 'Familias de personas con Alzheimer y otras demencias',
      modulo: 'Social — residencial, atención y expediente',
      web: 'https://alzheimerfuerteventura.org/',
    },
    {
      nombre: 'MPDL (Movimiento por la Paz)',
      sector: 'Acción social e intervención',
      modulo: 'Social — proyectos, casos y trazabilidad',
      web: 'https://www.mpdl.org/',
    },
  ];

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url: PAGE_URL,
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.href
            ? `${item.answer} ${item.linkLabel}: https://e-asy.es${item.href}`
            : item.answer,
        },
      })),
    }),
    []
  );

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Vulpin Suite',
    alternateName: ['Vulpin ERP', 'Vulpin 360', 'Vulpin'],
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'ERP modular en producción para pymes y entidades sociosanitarias: Business, Social, Care, Corps y Teams. Incluye automatización de procesos de gestión y digitalización / OCR documental. Pensado para datos especialmente sensibles.',
    url: PAGE_URL,
    provider: {
      '@type': 'Organization',
      name: 'E-ASY',
      url: 'https://e-asy.es',
    },
    offers: {
      '@type': 'Offer',
      url: PAGE_URL,
      availability: 'https://schema.org/InStock',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://e-asy.es/' },
      { '@type': 'ListItem', position: 2, name: 'Servicios', item: 'https://e-asy.es/servicios' },
      { '@type': 'ListItem', position: 3, name: 'Vulpin Suite', item: PAGE_URL },
    ],
  };

  return (
    <>
      <SEO
        title="Vulpin Suite | ERP modular en producción | E-ASY"
        description="Vulpin Suite (Vulpin ERP): Business, Social, Care, Corps y Teams. Unas 40 organizaciones ya lo usan. Pide la demo del módulo que te toca."
        keywords="Vulpin Suite, Vulpin ERP, Vulpin 360, demo Vulpin, ERP sector social, Vulpin Teams, ACUFADE, AFFA, MPDL"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-vulpin-faq" data={faqSchema} />
      <JsonLd id="jsonld-vulpin-product" data={productSchema} />
      <JsonLd id="jsonld-vulpin-breadcrumb" data={breadcrumbSchema} />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`vulpin-suite-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="vulpin-hero">
          <div className="vulpin-hero-content">
            <div className="vulpin-hero-text">
              <h1 className="vulpin-hero-title">Vulpin Suite</h1>
              <p className="vulpin-hero-subtitle">
                <strong>ERP modular</strong> en producción en unas{' '}
                <strong>40 organizaciones</strong>. <strong>Business</strong> para la
                pyme, <strong>Social</strong> y <strong>Care</strong> para el sector
                sociosanitario, <strong>Corps</strong> para voluntariado y{' '}
                <strong>Teams</strong> para el equipo.
              </p>
              <p className="vulpin-hero-subtitle">
                Hecho para <strong>datos especialmente sensibles</strong> (menores,
                dependencia, mujer, salud). La automatización de procesos y el OCR
                van <strong>dentro de Vulpin</strong>, no como un extra.
              </p>
              <div className="vulpin-hero-cta">
                <Link to="/contacto" className="vulpin-hero-button primary">
                  Solicitar una demo
                </Link>
                <a href="#modulos" className="vulpin-hero-button secondary">
                  Ver los módulos
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="vulpin-que-es">
          <div className="vulpin-que-es-content">
            <div className="vulpin-que-es-header">
              <h2 className="vulpin-que-es-title">Qué es Vulpin Suite</h2>
              <p className="vulpin-que-es-subtitle">
                Un ERP que se activa por módulos, no un monolito que obliga a
                cambiarlo todo el día uno. <strong>Business</strong> para pymes
                (facturación, tesorería, contabilidad). <strong>Social</strong> para
                servicios sociales, dependencia, menores y mujer.{' '}
                <strong>Care</strong> para centros residenciales y geriátricos.{' '}
                <strong>Corps</strong> para voluntariado. <strong>Teams</strong> para
                el portal de empleados: horario, permisos y nóminas.
              </p>
              <p className="vulpin-que-es-subtitle">
                Si el caso no encaja, no te lo colamos: integramos lo que ya tienes
                o buscamos otra base. Vulpin es prueba de que implantamos software
                real, no la única herramienta del cajón.
              </p>
            </div>
          </div>
        </section>

        <section className="vulpin-servicios" id="modulos">
          <div className="vulpin-servicios-content">
            <div className="vulpin-servicios-header">
              <h2 className="vulpin-servicios-title">Cinco módulos, un expediente</h2>
              <p className="vulpin-servicios-subtitle">
                Se encienden las que hacen falta. El dato sensible no se trata como
                un extra de “seguridad al final”.
              </p>
            </div>
            <div className="vulpin-servicios-grid vulpin-paquetes-grid">
              {modulos.map((modulo) => (
                <article key={modulo.id} className="vulpin-servicio-card">
                  <h3 className="servicio-title">{modulo.titulo}</h3>
                  <p className="servicio-description">{modulo.descripcion}</p>
                  <div className="servicio-casos">
                    <h4 className="casos-title">Incluye:</h4>
                    <ul className="casos-list">
                      {modulo.puntos.map((punto) => (
                        <li key={punto} className="caso-item">
                          {punto}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="vulpin-beneficios">
          <div className="vulpin-beneficios-content">
            <div className="vulpin-beneficios-header">
              <h2 className="vulpin-beneficios-title">
                Dentro del producto, no como servicio suelto
              </h2>
              <p className="vulpin-beneficios-subtitle">
                Lo que antes se vendía como categorías débiles (ERP genérico,
                automatización de gestión, OCR) aquí es función de Vulpin Suite.
              </p>
            </div>
            <div className="vulpin-beneficios-grid">
              {incluidoEnProducto.map((item) => (
                <div key={item.titulo} className="vulpin-beneficio-card">
                  <h3 className="beneficio-title">{item.titulo}</h3>
                  <p className="beneficio-description">{item.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="vulpin-sectores">
          <div className="vulpin-sectores-content">
            <div className="vulpin-sectores-header">
              <h2 className="vulpin-sectores-title">Quién lo usa</h2>
              <p className="vulpin-sectores-subtitle">
                Unas 40 organizaciones en producción. Estas tres están citadas como
                casos de referencia en el sector social.
              </p>
            </div>
            <div className="vulpin-sectores-grid">
              {referencias.map((ref) => (
                <article key={ref.nombre} className="vulpin-sector-card">
                  <h3 className="sector-title">
                    <a
                      href={ref.web}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ref.nombre}
                    </a>
                  </h3>
                  <p className="sector-description">
                    <strong>{ref.sector}.</strong> {ref.modulo}.
                  </p>
                </article>
              ))}
            </div>
            <p className="vulpin-ciber-callout">
              Más detalle de estos casos en{' '}
              <Link to="/casos">casos de referencia</Link>. El diferencial no es
              “tenemos un ERP”: es operar donde el dato es de{' '}
              <strong>categoría especial</strong>. Por eso Vulpin encaja con{' '}
              <Link to="/servicios/ciberseguridad-cumplimiento">
                Ciberseguridad y cumplimiento
              </Link>{' '}
              y, si el prototipo de IA hay que aterrizarlo sobre el expediente, con{' '}
              <Link to="/servicios/ia-en-produccion">IA en producción</Link>
              {' '}o{' '}
              <Link to="/ia-privada">IA privada</Link>
              . Si lo que buscáis es Dynamics 365, la ficha es{' '}
              <Link to="/servicios/business-central">Business Central</Link>
              ; Vulpin no se presenta como Dynamics. Criterio escrito:{' '}
              <Link to="/blog/business-central-o-vulpin-como-se-decide">
                cómo se decide BC o Vulpin
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="vulpin-faq">
          <div className="vulpin-faq-content">
            <div className="vulpin-faq-header">
              <h2 className="vulpin-faq-title">Preguntas frecuentes</h2>
            </div>
            <div className="vulpin-faq-list">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="vulpin-faq-item">
                  <summary>{item.question}</summary>
                  <p>
                    {item.answer}
                    {item.href && (
                      <>
                        {' '}
                        <Link to={item.href}>{item.linkLabel}</Link>.
                      </>
                    )}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="vulpin-cta">
          <div className="vulpin-cta-content">
            <h2 className="vulpin-cta-title">¿Quieres verlo con vuestro caso?</h2>
            <p className="vulpin-cta-subtitle">
              Demo adaptada a pymes (Business), RRHH (Teams) o a entidad social /
              centro (Social, Care, Corps). Sin postureo: si no encaja, te lo
              decimos.
            </p>
            <div className="vulpin-cta-buttons">
              <Link to="/contacto" className="vulpin-cta-button primary">
                Solicitar una demo
              </Link>
              <Link to="/contacto" className="vulpin-cta-button secondary">
                Hablar de nuestro caso
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default VulpinSuitePage;
